import { Card, Checkbox } from '@payaca/ambrosia';
import { FC } from 'react';

import EmailPreview from './EmailPreview';

import SenderChoiceField from '@/ui/components/senderChoice/SenderChoiceField';
import { type SenderChoiceControl } from '@/ui/components/senderChoice/useSenderChoice';
import TruncatedShadowDomRenderer from '@payaca/components/shadowDomRenderer/TruncatedShadowDomRenderer';
import './SendableDocumentEmailControl.sass';

type Props = {
  emailSubject: string;
  senderChoice?: SenderChoiceControl;
  emailState: {
    // will take priority over the template
    htmlTemplate?: string;
    sendMeACopy?: boolean;
    emailCopy: {
      preButton: string;
      postButton: string;
    };
  };
  onChange: (value?: { [key: string]: any }) => void;
  sendToAddress?: string;
  buttonText: string;
  /**
   * Show the "Send me a copy" checkbox. Only Service Plan invitations still
   * offer it; the other send flows rely on the client portal preview to show
   * the sender what the Customer sees.
   */
  showSendMeACopy?: boolean;
};

const SendableDocumentEmailControl: FC<Props> = (props) => {
  const {
    emailSubject,
    senderChoice,
    emailState,
    onChange,
    sendToAddress,
    buttonText,
    showSendMeACopy = false,
  } = props;

  return (
    <div className="sendable-document-email-control">
      <div className="addressee-and-subject-container">
        {senderChoice && (
          <div className="sender-container">
            <div>
              <strong>From:</strong>
            </div>
            <div className="sender">
              <SenderChoiceField {...senderChoice} />
            </div>
          </div>
        )}
        <div className="addressee-container">
          {sendToAddress && (
            <>
              <div>
                <strong>To:</strong>
              </div>
              <span>{sendToAddress}</span>
            </>
          )}
        </div>
        <div className="subject-container">
          <div>
            <strong>Subject:</strong>
          </div>
          <div>{emailSubject}</div>
        </div>
      </div>
      {showSendMeACopy && (
        <div className="email-options-container">
          <Checkbox
            label="Send me a copy"
            onChange={(e) => onChange({ sendMeACopy: e.target.checked })}
            checked={emailState.sendMeACopy ?? false}
          />
        </div>
      )}

      {emailState.htmlTemplate ? (
        <Card className="overflow-hidden">
          <Card.Body>
            <TruncatedShadowDomRenderer html={emailState.htmlTemplate} />
          </Card.Body>
        </Card>
      ) : (
        <EmailPreview
          buttonText={buttonText}
          emailCopy={emailState.emailCopy}
          onChange={(value) =>
            onChange({ emailCopy: { ...emailState.emailCopy, ...value } })
          }
        />
      )}
    </div>
  );
};

export default SendableDocumentEmailControl;
